// LogisticsBilling.jsx
import React, { useState, useEffect } from 'react';
import { 
    FaPlus, FaEdit, FaTrash, FaTimes, FaSave, 
    FaSpinner, FaSearch, FaFilter, FaToggleOn, FaToggleOff,
    FaCheckCircle, FaClock, FaDollarSign, FaTruck, FaMapPin
} from 'react-icons/fa';
import {
    createLogisticsBilling,
    getAllLogisticsBilling,
    getLogisticsBillingById,
    updateLogisticsBilling,
    deleteLogisticsBilling,
    ENUMS
} from '../../../Servicefile/logisticsBillingService.jsx';

const LogisticsBilling = () => {
    const [billings, setBillings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        id: '',
        vehicleStatus: 'TWO_WHEELER',
        vehicleType: 'BIKE',
        basePrice: '',
        pricePerKm: '',
        minimumFare: '',
        waitingChargePerMinute: '',
        cancellationCharge: '',
        active: true
    });

    useEffect(() => {
        fetchBillings();
    }, []);

    const fetchBillings = async () => {
        setLoading(true);
        try {
            const data = await getAllLogisticsBilling();
            setBillings(data || []);
        } catch (error) {
            console.error('Error fetching billings:', error);
            alert('Failed to fetch logistics billing data');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({
            id: '',
            vehicleStatus: 'TWO_WHEELER',
            vehicleType: 'BIKE',
            basePrice: '',
            pricePerKm: '',
            minimumFare: '',
            waitingChargePerMinute: '',
            cancellationCharge: '',
            active: true
        });
        setEditingItem(null);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.vehicleStatus) {
            alert('Please select vehicle status');
            return;
        }
        if (!formData.vehicleType) {
            alert('Please select vehicle type');
            return;
        }
        if (!formData.basePrice || parseFloat(formData.basePrice) < 0) {
            alert('Please enter a valid base price');
            return;
        }

        const requestData = {
            vehicleStatus: formData.vehicleStatus,
            vehicleType: formData.vehicleType,
            basePrice: parseFloat(formData.basePrice) || 0,
            pricePerKm: parseFloat(formData.pricePerKm) || 0,
            minimumFare: parseFloat(formData.minimumFare) || 0,
            waitingChargePerMinute: parseFloat(formData.waitingChargePerMinute) || 0,
            cancellationCharge: parseFloat(formData.cancellationCharge) || 0,
            active: formData.active
        };

        setSubmitting(true);
        try {
            await createLogisticsBilling(requestData);
            alert('Logistics billing created successfully!');
            setShowCreateModal(false);
            resetForm();
            fetchBillings();
        } catch (error) {
            console.error('Error creating billing:', error);
            alert(error.response?.data?.message || 'Failed to create billing');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = async (id) => {
        try {
            const data = await getLogisticsBillingById(id);
            setEditingItem(data);
            setFormData({
                id: data.id || '',
                vehicleStatus: data.vehicleStatus || 'TWO_WHEELER',
                vehicleType: data.vehicleType || 'BIKE',
                basePrice: data.basePrice?.toString() || '',
                pricePerKm: data.pricePerKm?.toString() || '',
                minimumFare: data.minimumFare?.toString() || '',
                waitingChargePerMinute: data.waitingChargePerMinute?.toString() || '',
                cancellationCharge: data.cancellationCharge?.toString() || '',
                active: data.active ?? true
            });
            setShowEditModal(true);
        } catch (error) {
            console.error('Error fetching billing details:', error);
            alert('Failed to fetch billing details');
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        
        const requestData = {
            vehicleStatus: formData.vehicleStatus,
            vehicleType: formData.vehicleType,
            basePrice: parseFloat(formData.basePrice) || 0,
            pricePerKm: parseFloat(formData.pricePerKm) || 0,
            minimumFare: parseFloat(formData.minimumFare) || 0,
            waitingChargePerMinute: parseFloat(formData.waitingChargePerMinute) || 0,
            cancellationCharge: parseFloat(formData.cancellationCharge) || 0,
            active: formData.active
        };

        setSubmitting(true);
        try {
            await updateLogisticsBilling(formData.id, requestData);
            alert('Logistics billing updated successfully!');
            setShowEditModal(false);
            resetForm();
            fetchBillings();
        } catch (error) {
            console.error('Error updating billing:', error);
            alert(error.response?.data?.message || 'Failed to update billing');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this billing record?')) return;
        
        try {
            await deleteLogisticsBilling(id);
            alert('Billing record deleted successfully!');
            fetchBillings();
        } catch (error) {
            console.error('Error deleting billing:', error);
            alert('Failed to delete billing record');
        }
    };

    const handleToggleActive = async (id, currentStatus) => {
        try {
            const item = await getLogisticsBillingById(id);
            const updatedData = {
                ...item,
                active: !currentStatus
            };
            await updateLogisticsBilling(id, {
                vehicleStatus: updatedData.vehicleStatus,
                vehicleType: updatedData.vehicleType,
                basePrice: updatedData.basePrice,
                pricePerKm: updatedData.pricePerKm,
                minimumFare: updatedData.minimumFare,
                waitingChargePerMinute: updatedData.waitingChargePerMinute,
                cancellationCharge: updatedData.cancellationCharge,
                active: updatedData.active
            });
            fetchBillings();
        } catch (error) {
            console.error('Error toggling status:', error);
            alert('Failed to update status');
        }
    };

    // Filter billings
    const filteredBillings = billings.filter(item => {
        const matchesSearch = 
            item.vehicleType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.vehicleStatus?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesFilter = filterStatus === 'all' || 
            (filterStatus === 'active' && item.active) ||
            (filterStatus === 'inactive' && !item.active);
        
        return matchesSearch && matchesFilter;
    });

    // Get status counts
    const totalCount = billings.length;
    const activeCount = billings.filter(b => b.active).length;
    const inactiveCount = billings.filter(b => !b.active).length;

    // Format currency
    const formatCurrency = (value) => {
        return value !== undefined && value !== null ? `₹${Number(value).toFixed(2)}` : '₹0.00';
    };

    return (
        <div style={containerStyle}>
            {/* Header */}
            <div style={headerStyle}>
                <div style={headerLeftStyle}>
                    <h2 style={titleStyle}>Logistics Billing</h2>
                    <span style={countStyle}>{totalCount} records</span>
                </div>
                <button
                    onClick={() => { resetForm(); setShowCreateModal(true); }}
                    style={createButtonStyle}
                >
                    <FaPlus size={14} />
                    Add Billing
                </button>
            </div>

            {/* Stats Cards */}
            <div style={statsContainerStyle}>
                <div style={statCardStyle}>
                    <div style={{ ...statIconStyle, background: '#e8f0fe' }}>
                        <FaTruck size={20} color="#1a73e8" />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{totalCount}</div>
                        <div style={statLabelStyle}>Total</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconStyle, background: '#d4edda' }}>
                        <FaCheckCircle size={20} color="#28a745" />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{activeCount}</div>
                        <div style={statLabelStyle}>Active</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconStyle, background: '#f8d7da' }}>
                        <FaTimes size={20} color="#dc3545" />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{inactiveCount}</div>
                        <div style={statLabelStyle}>Inactive</div>
                    </div>
                </div>
            </div>

            {/* Search and Filter */}
            <div style={searchFilterContainerStyle}>
                <div style={searchContainerStyle}>
                    <FaSearch style={searchIconStyle} />
                    <input
                        type="text"
                        placeholder="Search by vehicle type or status..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={searchInputStyle}
                    />
                </div>
                <div style={filterContainerStyle}>
                    <FaFilter style={filterIconStyle} />
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={filterSelectStyle}
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
           
            </div>

            {/* Table */}
            <div style={tableCardStyle}>
                {loading ? (
                    <div style={loadingStyle}>
                        <FaSpinner size={30} style={{ animation: 'spin 1s linear infinite' }} />
                        <p>Loading billing data...</p>
                    </div>
                ) : (
                    <div style={tableWrapperStyle}>
                        <table style={tableStyle}>
                            <thead>
                                <tr style={{backgroundColor:"#e66d33", color:"white", }}>
                                    <th style={{ width: '5%', textAlign: 'center', padding:"10px" }}>S.No</th>
                                    <th style={{ width: '12%', textAlign: 'center' , padding:"10px" }}>Vehicle Status</th>
                                    <th style={{ width: '12%', textAlign: 'center' , padding:"10px" }}>Vehicle Type</th>
                                    <th style={{ width: '10%', textAlign: 'center', padding:"10px"  }}>Base Price</th>
                                    <th style={{ width: '10%', textAlign: 'center', padding:"10px"  }}>Price/Km</th>
                                    <th style={{ width: '10%', textAlign: 'center', padding:"10px"  }}>Min Fare</th>
                                    <th style={{ width: '10%', textAlign: 'center' , padding:"10px" }}>Waiting Charge</th>
                                    <th style={{ width: '10%', textAlign: 'center' , padding:"10px" }}>Cancellation</th>
                                    <th style={{ width: '8%', textAlign: 'center' , padding:"10px" }}>Status</th>
                                    <th style={{ width: '13%', textAlign: 'center', padding:"10px"  }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBillings.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" style={emptyStateStyle}>
                                            No billing records found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredBillings.map((item, index) => (
                                        <tr key={item.id} style={tableRowStyle}>
                                            <td style={{ ...tableCellStyle, textAlign: 'center' }}>{index + 1}</td>
                                            <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                                                <span style={statusBadgeStyle}>
                                                    {item.vehicleStatus?.replace('_', ' ')}
                                                </span>
                                            </td>
                                            <td style={{ ...tableCellStyle, textAlign: 'center', fontWeight: 600 }}>
                                                {item.vehicleType?.replace('_', ' ')}
                                            </td>
                                            <td style={{ ...tableCellStyle, textAlign: 'center', fontWeight: 600, color: '#e66d33' }}>
                                                {formatCurrency(item.basePrice)}
                                            </td>
                                            <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                                                {formatCurrency(item.pricePerKm)}
                                            </td>
                                            <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                                                {formatCurrency(item.minimumFare)}
                                            </td>
                                            <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                                                {formatCurrency(item.waitingChargePerMinute)}
                                            </td>
                                            <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                                                {formatCurrency(item.cancellationCharge)}
                                            </td>
                                            <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                                                <button
                                                    onClick={() => handleToggleActive(item.id, item.active)}
                                                    style={toggleButtonStyle}
                                                >
                                                    {item.active ? (
                                                        <span style={activeToggleStyle}>
                                                            <FaToggleOn size={24} color="#28a745" />
                                                            <span style={{ fontSize: '11px', color: '#28a745' }}>Active</span>
                                                        </span>
                                                    ) : (
                                                        <span style={inactiveToggleStyle}>
                                                            <FaToggleOff size={24} color="#dc3545" />
                                                            <span style={{ fontSize: '11px', color: '#dc3545' }}>Inactive</span>
                                                        </span>
                                                    )}
                                                </button>
                                            </td>
                                            <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                                                <div style={actionGroupStyle}>
                                                    <button
                                                        onClick={() => handleEdit(item.id)}
                                                        style={editButtonStyle}
                                                        title="Edit"
                                                    >
                                                        <FaEdit size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.id)}
                                                        style={deleteButtonStyle}
                                                        title="Delete"
                                                    >
                                                        <FaTrash size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <BillingModal
                    formData={formData}
                    setFormData={setFormData}
                    handleInputChange={handleInputChange}
                    handleSubmit={handleCreate}
                    setShowModal={setShowCreateModal}
                    submitting={submitting}
                    resetForm={resetForm}
                    title="Create Logistics Billing"
                    submitLabel="Create Billing"
                    ENUMS={ENUMS}
                />
            )}

            {/* Edit Modal */}
            {showEditModal && (
                <BillingModal
                    formData={formData}
                    setFormData={setFormData}
                    handleInputChange={handleInputChange}
                    handleSubmit={handleUpdate}
                    setShowModal={setShowEditModal}
                    submitting={submitting}
                    resetForm={resetForm}
                    title="Edit Logistics Billing"
                    submitLabel="Update Billing"
                    ENUMS={ENUMS}
                    isEdit={true}
                />
            )}

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

// ─── Billing Modal Component ──────────────────────────────────

const BillingModal = ({
    formData,
    setFormData,
    handleInputChange,
    handleSubmit,
    setShowModal,
    submitting,
    resetForm,
    title,
    submitLabel,
    ENUMS,
    isEdit = false
}) => {
    return (
        <div style={modalOverlayStyle} onClick={() => setShowModal(false)}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <div style={modalHeaderStyle}>
                    <h3 style={modalTitleStyle}>{title}</h3>
                    <button onClick={() => { resetForm(); setShowModal(false); }} style={modalCloseStyle}>
                        <FaTimes />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={modalBodyStyle}>
                    {/* Vehicle Status */}
                    <div style={formGroupStyle}>
                        <label style={labelStyle}>Vehicle Status *</label>
                        <select
                            name="vehicleStatus"
                            value={formData.vehicleStatus}
                            onChange={handleInputChange}
                            style={selectStyle}
                            required
                        >
                            {ENUMS.vehicleStatus.map(status => (
                                <option key={status} value={status}>
                                    {status.replace('_', ' ')}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Vehicle Type */}
                    <div style={formGroupStyle}>
                        <label style={labelStyle}>Vehicle Type *</label>
                        <select
                            name="vehicleType"
                            value={formData.vehicleType}
                            onChange={handleInputChange}
                            style={selectStyle}
                            required
                        >
                            {ENUMS.vehicleType.map(type => (
                                <option key={type} value={type}>
                                    {type.replace('_', ' ')}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Price Fields */}
                    <div style={formRowStyle}>
                        <div style={formGroupStyle}>
                            <label style={labelStyle}>Base Price (₹) *</label>
                            <input
                                name="basePrice"
                                type="number"
                                value={formData.basePrice}
                                onChange={handleInputChange}
                                style={inputStyle}
                                min="0"
                                step="0.01"
                                required
                            />
                        </div>
                        <div style={formGroupStyle}>
                            <label style={labelStyle}>Price Per Km (₹)</label>
                            <input
                                name="pricePerKm"
                                type="number"
                                value={formData.pricePerKm}
                                onChange={handleInputChange}
                                style={inputStyle}
                                min="0"
                                step="0.01"
                            />
                        </div>
                    </div>

                    <div style={formRowStyle}>
                        <div style={formGroupStyle}>
                            <label style={labelStyle}>Minimum Fare (₹)</label>
                            <input
                                name="minimumFare"
                                type="number"
                                value={formData.minimumFare}
                                onChange={handleInputChange}
                                style={inputStyle}
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div style={formGroupStyle}>
                            <label style={labelStyle}>Waiting Charge/Minute (₹)</label>
                            <input
                                name="waitingChargePerMinute"
                                type="number"
                                value={formData.waitingChargePerMinute}
                                onChange={handleInputChange}
                                style={inputStyle}
                                min="0"
                                step="0.01"
                            />
                        </div>
                    </div>

                    <div style={formGroupStyle}>
                        <label style={labelStyle}>Cancellation Charge (₹)</label>
                        <input
                            name="cancellationCharge"
                            type="number"
                            value={formData.cancellationCharge}
                            onChange={handleInputChange}
                            style={inputStyle}
                            min="0"
                            step="0.01"
                        />
                    </div>

                    {/* Active Status Toggle */}
                    <div style={formGroupStyle}>
                        <label style={labelStyle}>Status</label>
                        <div style={toggleContainerStyle}>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, active: true }))}
                                style={{
                                    ...toggleOptionStyle,
                                    background: formData.active ? '#e66d33' : '#f0f0f0',
                                    color: formData.active ? 'white' : '#666'
                                }}
                            >
                                <FaCheckCircle size={14} /> Active
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, active: false }))}
                                style={{
                                    ...toggleOptionStyle,
                                    background: !formData.active ? '#dc3545' : '#f0f0f0',
                                    color: !formData.active ? 'white' : '#666'
                                }}
                            >
                                <FaTimes size={14} /> Inactive
                            </button>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div style={formActionStyle}>
                        <button
                            type="button"
                            onClick={() => { resetForm(); setShowModal(false); }}
                            style={cancelButtonStyle}
                        >
                            Cancel
                        </button>
                        <button type="submit" style={submitButtonStyle} disabled={submitting}>
                            {submitting ? (
                                <>
                                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={14} />
                                    {isEdit ? 'Updating...' : 'Creating...'}
                                </>
                            ) : (
                                <>
                                    {isEdit ? <FaSave size={14} /> : <FaPlus size={14} />}
                                    {submitLabel}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ─── Styles ──────────────────────────────────────────────────────

const containerStyle = {
    padding: '20px',
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '12px',
};

const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
};

const titleStyle = {
    fontSize: '24px',
    fontWeight: 700,
    color: '#333',
    margin: 0,
};

const countStyle = {
    fontSize: '14px',
    color: '#888',
    background: '#f0f0f0',
    padding: '4px 12px',
    borderRadius: '20px',
};

const createButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    background: '#e66d33',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const statsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '20px',
};

const statCardStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#fff',
    padding: '16px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid #f0ebe6',
};

const statIconStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const statNumberStyle = {
    fontSize: '22px',
    fontWeight: 700,
    color: '#333',
};

const statLabelStyle = {
    fontSize: '13px',
    color: '#888',
};

const searchFilterContainerStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    flexWrap: 'wrap',
    alignItems: 'center',
};

const searchContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    background: '#fff',
    border: '1px solid #e0d6ce',
    borderRadius: '10px',
    padding: '0 14px',
};

const searchIconStyle = {
    color: '#999',
    marginRight: '10px',
};

const searchInputStyle = {
    flex: 1,
    padding: '12px 0',
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    background: 'transparent',
};

const filterContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    border: '1px solid #e0d6ce',
    borderRadius: '10px',
    padding: '0 14px',
};

const filterIconStyle = {
    color: '#999',
    marginRight: '10px',
};

const filterSelectStyle = {
    padding: '12px 8px',
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    background: 'transparent',
    cursor: 'pointer',
};

const refreshButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    background: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const tableCardStyle = {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    border: '1px solid #f0ebe6',
};

const tableWrapperStyle = {
    overflowX: 'auto',
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '1100px',
};

const tableRowStyle = {
    borderBottom: '1px solid #f0ebe6',
    transition: 'background 0.2s ease',
};

const tableCellStyle = {
    padding: '12px 14px',
    fontSize: '13px',
    color: '#333',
};

const emptyStateStyle = {
    padding: '40px',
    textAlign: 'center',
    color: '#999',
};

const loadingStyle = {
    padding: '40px',
    textAlign: 'center',
    color: '#666',
    '& p': {
        marginTop: '12px',
    },
};

const statusBadgeStyle = {
    display: 'inline-block',
    padding: '2px 10px',
    background: 'rgba(230, 126, 34, 0.1)',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#e66d33',
};

const toggleButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
};

const activeToggleStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: 500,
};

const inactiveToggleStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: 500,
};

const actionGroupStyle = {
    display: 'flex',
    gap: '6px',
    justifyContent: 'center',
};

const editButtonStyle = {
    padding: '6px 10px',
    background: 'rgba(40, 167, 69, 0.1)',
    color: '#28a745',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const deleteButtonStyle = {
    padding: '6px 10px',
    background: 'rgba(220, 53, 69, 0.1)',
    color: '#dc3545',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

// ─── Modal Styles ──────────────────────────────────────────────

const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
};

const modalStyle = {
    background: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '550px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

const modalHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #f0ebe6',
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 1,
};

const modalTitleStyle = {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: '#333',
};

const modalCloseStyle = {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#999',
    padding: '4px 8px',
};

const modalBodyStyle = {
    padding: '24px',
};

const formRowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
};

const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '16px',
};

const labelStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#555',
};

const inputStyle = {
    padding: '10px 12px',
    border: '1px solid #e0d6ce',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
};

const selectStyle = {
    ...inputStyle,
    background: '#fff',
    appearance: 'auto',
};

const toggleContainerStyle = {
    display: 'flex',
    gap: '8px',
};

const toggleOptionStyle = {
    flex: 1,
    padding: '8px 12px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'all 0.2s ease',
};

const formActionStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'space-between',
    marginTop: '8px',
};

const cancelButtonStyle = {
    padding: '10px 24px',
    background: '#f0f0f0',
    color: '#666',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const submitButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 24px',
    background: '#e66d33',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

export default LogisticsBilling;